<template>
  <VaDropdown :offset="[13, 0]" class="notification-dropdown" stick-to-edges :close-on-content-click="false">
    <template #anchor>
      <VaButton class="rd-icon-only-button" preset="secondary" color="textPrimary" aria-label="Notifications" title="Notifications">
        <VaBadge overlap :text="String(notifications.length)">
          <VaIconNotification class="notification-dropdown__icon" />
        </VaBadge>
      </VaButton>
    </template>
    <VaDropdownContent class="notification-dropdown__content">
      <section class="notification-dropdown__panel">
        <VaList class="notification-dropdown__list">
          <template v-for="(item, index) in notificationsWithRelativeTime" :key="item.id">
            <VaListItem class="notification-dropdown__item text-base" :title="notificationDetails(item)">
              <VaListItemSection icon class="notification-dropdown__indicator mx-0 p-0">
                <VaIcon name="notifications" :color="severityColor(item.severity)" />
              </VaListItemSection>
              <VaListItemSection class="notification-dropdown__body">
                <div class="notification-dropdown__title" :title="item.title">{{ item.title }}</div>
                <div class="notification-dropdown__message" :title="item.message">{{ item.message }}</div>
              </VaListItemSection>
              <VaListItemSection icon class="notification-dropdown__time mx-1" :title="item.updateTimestamp">
                {{ item.updateTimestamp }}
              </VaListItemSection>
            </VaListItem>
            <VaListSeparator v-if="item.separator && index !== notificationsWithRelativeTime.length - 1" class="mx-3" />
          </template>
        </VaList>

        <div class="notification-actions">
          <VaButton preset="secondary" color="danger" icon="delete_sweep" :disabled="notifications.length === 0" @click="clearNotifications">
            Clear
          </VaButton>
        </div>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import VaIconNotification from '../../../icons/VaIconNotification.vue'
import { fetchPanelNotifications, isAdminAuthenticated, type PanelNotification } from '../../../../services/repodepot-api'

const { locale } = useI18n()

const maxVisibleNotifications = 5
const rtf = new Intl.RelativeTimeFormat(locale.value, { style: 'short' })
const notifications = ref<PanelNotification[]>([])
const NOTIFICATIONS_CLEARED_AT_KEY = 'rd_admin_notifications_cleared_at'

function readClearedAt(): number {
  const raw = localStorage.getItem(NOTIFICATIONS_CLEARED_AT_KEY)
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

function writeClearedAt(value: number): void {
  localStorage.setItem(NOTIFICATIONS_CLEARED_AT_KEY, String(value))
}

const TIME_NAMES = {
  second: 1000,
  minute: 1000 * 60,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
  week: 1000 * 60 * 60 * 24 * 7,
  month: 1000 * 60 * 60 * 24 * 30,
  year: 1000 * 60 * 60 * 24 * 365,
}

const getTimeName = (differenceTime: number) => {
  return Object.keys(TIME_NAMES).reduce(
    (acc, key) => (TIME_NAMES[key as keyof typeof TIME_NAMES] < differenceTime ? key : acc),
    'month',
  ) as keyof typeof TIME_NAMES
}

const notificationsWithRelativeTime = computed(() => {
  const sorted = [...notifications.value].sort(
    (left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime(),
  )
  const list = sorted.slice(0, maxVisibleNotifications)

  return list.map((item, index) => {
    const timeDifference = Math.round(new Date().getTime() - new Date(item.timestamp).getTime())
    const timeName = getTimeName(timeDifference)

    let separator = false

    const nextItem = list[index + 1]
    if (nextItem) {
      const nextItemDifference = Math.round(new Date().getTime() - new Date(nextItem.timestamp).getTime())
      const nextItemTimeName = getTimeName(nextItemDifference)

      if (timeName !== nextItemTimeName) {
        separator = true
      }
    }

    return {
      ...item,
      updateTimestamp: rtf.format(-1 * Math.round(timeDifference / TIME_NAMES[timeName]), timeName),
      separator,
    }
  })
})

function severityColor(severity: string): string {
  if (severity === 'warning') {
    return 'warning'
  }
  if (severity === 'info') {
    return 'info'
  }
  if (severity === 'success') {
    return 'success'
  }
  return 'secondary'
}

function notificationDetails(item: PanelNotification & { updateTimestamp: string }): string {
  return [item.title, item.message, item.updateTimestamp].filter(Boolean).join('\n')
}

async function loadNotifications() {
  if (!isAdminAuthenticated()) {
    notifications.value = []
    return
  }

  try {
    const clearedAt = readClearedAt()
    const fetched = await fetchPanelNotifications(20)
    notifications.value = fetched.filter((item) => {
      const timestampMs = new Date(item.timestamp).getTime()
      if (!Number.isFinite(timestampMs)) {
        return true
      }
      return timestampMs > clearedAt
    })
  } catch {
    notifications.value = []
  }
}

function clearNotifications() {
  writeClearedAt(Date.now())
  notifications.value = []
}

onMounted(() => {
  void loadNotifications()
})
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}

.notification-actions {
  display: flex;
  justify-content: flex-end;
}

.notification-dropdown__content {
  width: min(360px, calc(100vw - 2rem));
  max-width: min(360px, calc(100vw - 2rem));
  overflow-x: hidden;
}

.notification-dropdown__panel {
  padding: 1rem;
  overflow-x: hidden;
}

.notification-dropdown__list {
  margin-bottom: 0.5rem;
  overflow-x: hidden;
}

.notification-dropdown__item {
  max-width: 100%;
  overflow: hidden;
}

.notification-dropdown__item :deep(.va-list-item__inner) {
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 1fr) minmax(0, auto);
  column-gap: 0.75rem;
  align-items: start;
  max-width: 100%;
  overflow: hidden;
}

.notification-dropdown__indicator,
.notification-dropdown__body,
.notification-dropdown__time {
  min-width: 0;
}

.notification-dropdown__body {
  max-width: 100%;
}

.notification-dropdown__title,
.notification-dropdown__message,
.notification-dropdown__time {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-dropdown__title {
  font-weight: 600;
}

.notification-dropdown__time {
  max-width: 5.5rem;
  text-align: right;
}
</style>
